import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import './User.css';

export default function EditProfile() {
  const { user, updateProfile } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const validate = () => {
    if (!formData.name || !formData.phone) {
      setError('Name and phone are required');
      return false;
    }

    const wantsPasswordChange = formData.currentPassword || formData.newPassword || formData.confirmPassword;

    if (wantsPasswordChange) {
      if (!formData.currentPassword) {
        setError('Current password is required to change password');
        return false;
      }

      if (!formData.newPassword) {
        setError('New password is required');
        return false;
      }

      if (formData.newPassword.length < 6) {
        setError('New password must be at least 6 characters');
        return false;
      }

      if (formData.newPassword !== formData.confirmPassword) {
        setError('New password and confirm password do not match');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        city: formData.city
      };

      const wantsPasswordChange = formData.currentPassword || formData.newPassword;
      if (wantsPasswordChange) {
        payload.currentPassword = formData.currentPassword;
        payload.newPassword = formData.newPassword;
      }

      const result = await updateProfile(payload);

      if (result.success) {
        setSuccess(result.message || 'Profile updated successfully');
        
        // Clear password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));

        setTimeout(() => {
          navigate('/user/profile');
        }, 1500);
      } else {
        setError(result.message || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-edit-profile">
      <div className="container">
        <h1 className="page-title">
          <span className="title-icon">✏️</span>
          Edit Profile
        </h1>

        <form onSubmit={handleSubmit} className="edit-profile-form">
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span className="alert-text">{error}</span>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span className="alert-icon">✅</span>
              <span className="alert-text">{success}</span>
            </div>
          )}

          {/* Account Info */}
          <div className="card">
            <h3 className="card-title">Account Information</h3>

            <div className="readonly-field">
              <label>Email</label>
              <div className="readonly-value">{user?.email}</div>
            </div>

            <div className="readonly-field">
              <label>Role</label>
              <div className="readonly-value">{user?.role?.toUpperCase()}</div>
            </div>
          </div>

          {/* Personal Info */}
          <div className="card">
            <h3 className="card-title">Personal Information</h3>

            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter your address"
                rows="3"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter your city"
                disabled={loading}
              />
            </div>
          </div>

          {/* Change Password */}
          <div className="card">
            <h3 className="card-title">Change Password (Optional)</h3>

            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password (min 6 characters)"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className={`btn btn-primary ${loading ? 'btn-loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-small"></span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/user/profile')}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}